import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, InputNumber, Select, Row, Col, Radio, Alert } from 'antd';
import { PoundOutlined, FileTextOutlined, UserOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { createLoanRequest, getTeamMembers, getAllEmployees } from '../../redux/rtk/features/loanRequest/loanRequestSlice';
import { getAllContractDetail } from '../../redux/rtk/features/contactDetail/contractDetailSlice';

const { TextArea } = Input;
const { Option } = Select;

const ApplyLoan = ({ userRole, userId }) => {
    const [form] = Form.useForm();
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [loanType, setLoanType] = useState('advance'); // 'loan' or 'advance' — default Advance
    const dispatch = useDispatch();
    const { loading, teamMembers, allEmployees } = useSelector((state) => state.loanRequest);
    const { contractDetail } = useSelector((state) => state.contractDetail);

    useEffect(() => {
        // Fetch team members for managers and admins
        if (userRole === 'manager' || userRole === 'admin' || userRole === 'hr') {
            fetchTeamMembers();
        }
        
        // Fetch contract details for all employees
        dispatch(getAllContractDetail());
    }, [userRole, userId, dispatch]);

    // Initialize form with default values (Advance Payment)
    useEffect(() => {
        form.setFieldsValue({
            requestType: 'advance',
            returningMonths: 1,
        });
    }, [form]);

    const fetchTeamMembers = () => {
        if (userRole === 'admin' || userRole === 'hr') {
            // Get all employees for admin/HR
            dispatch(getAllEmployees());
        } else if (userRole === 'manager') {
            // Get team members for manager
            dispatch(getTeamMembers(userId));
        }
    };

    // Helper function to get monthly salary for a specific employee
    const getEmployeeMonthlySalary = (employeeId) => {
        if (!contractDetail || !Array.isArray(contractDetail)) {
            return null;
        }
        
        const employeeContract = contractDetail.find(contract => 
            contract.userId === parseInt(employeeId) && contract.status === true
        );
        
        return employeeContract ? employeeContract.monthlySalary : null;
    };

    // Helper function to check if employee has contract details
    const hasContractDetails = (employeeId) => {
        if (!contractDetail || !Array.isArray(contractDetail)) {
            return false;
        }
        
        const employeeContract = contractDetail.find(contract => 
            contract.userId === parseInt(employeeId) && contract.status === true
        );
        
        return !!employeeContract;
    };

    const onFinish = async (values) => {
        try {
            const targetUserId = selectedEmployee ? selectedEmployee : parseInt(userId);
            
            // Check for contract details if it's an advance payment
            if (loanType === 'advance' && !hasContractDetails(targetUserId)) {
                message.error('Contract details are not found, contact to admin.');
                return;
            }

            const requestData = {
                userId: targetUserId,
                requestedAmount: values.requestedAmount,
                purpose: values.purpose || (loanType === 'advance' ? 'Advance Payment' : ''),
                returningMonths: values.returningMonths,
                requestType: loanType,
                purposeCategory: values.purposeCategory || (loanType === 'advance' ? 'advance' : ''),
            };

            const response = await dispatch(createLoanRequest(requestData));

            if (response.payload?.message === "success") {
                form.resetFields();
                setSelectedEmployee(null);
                setLoanType('advance'); // Reset to default (Advance)
                form.setFieldsValue({
                    requestType: 'advance',
                    returningMonths: 1,
                });
                message.success(loanType === 'advance' ? 'Advance payment request submitted successfully!' : 'Loan request submitted successfully!');
            }
        } catch (error) {
            console.error('Error submitting loan request:', error);
        }
    };

    const onFinishFailed = (errorInfo) => {
        console.log('Failed:', errorInfo);
        message.error('Please fill in all required fields correctly.');
    };

    const handleEmployeeChange = (value) => {
        setSelectedEmployee(value);
        
        // Update the form field value to ensure consistency
        form.setFieldsValue({
            employeeId: value
        });
        
        // If Advance Payment is selected, prefilled loan amount and set returning months to 1
        if (loanType === 'advance') {
            const monthlySalary = getEmployeeMonthlySalary(value);
            form.setFieldsValue({
                employeeId: value,
                requestedAmount: monthlySalary || 0, // Use monthly salary or fallback to 0
                returningMonths: 1
            });
        }
    };

    const handleLoanTypeChange = (e) => {
        const newLoanType = e.target.value;
        setLoanType(newLoanType);
        
        // Update form field for request type
        form.setFieldsValue({
            requestType: newLoanType
        });
        
        // If Advance Payment is selected, prefilled values
        if (newLoanType === 'advance') {
            const employeeId = selectedEmployee || userId; // Use selected employee or current user
            const monthlySalary = getEmployeeMonthlySalary(employeeId);
            form.setFieldsValue({
                requestType: newLoanType,
                requestedAmount: monthlySalary || 0, // Use monthly salary or fallback to 0
                returningMonths: 1
            });
        }
        
        // Clear form if switching to loan
        if (newLoanType === 'loan') {
            form.setFieldsValue({
                requestType: newLoanType,
                requestedAmount: undefined,
                returningMonths: undefined
            });
        }
        
        // Trigger validation for the amount field when loan type changes
        form.validateFields(['requestedAmount']);
    };

    const canApplyForOthers = userRole === 'manager' || userRole === 'admin' || userRole === 'hr';
    
    // Check if current user/selected employee has contract details for advance payment warning
    const showContractWarning = () => {
        if (loanType !== 'advance') return false;
        const targetUserId = selectedEmployee || userId;
        return !hasContractDetails(targetUserId);
    };

    return (
        // <Card title="Apply for Loan/ Advance" className="apply-loan-card">
        <Card title="Apply for Advance" className="apply-loan-card">

            <Form
                form={form}
                name="applyLoanForm"
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                layout="vertical"
                requiredMark={false}
            >
                {canApplyForOthers && (
                    <Form.Item
                        label="Select Employee"
                        name="employeeId"
                        rules={[{ required: canApplyForOthers, message: 'Please select an employee!' }]}
                        validateTrigger={['onBlur', 'onSubmit']}
                    >
                        <Select
                            placeholder="Select employee to apply advance for"
                            showSearch
                            allowClear
                            value={selectedEmployee}
                            onChange={handleEmployeeChange}
                            onClear={() => {
                                setSelectedEmployee(null);
                                form.setFieldsValue({
                                    employeeId: undefined,
                                    requestedAmount: undefined,
                                    returningMonths: undefined
                                });
                            }}
                            filterOption={(input, option) => {
                                const searchText = input.toLowerCase();
                                const member = (userRole === 'admin' || userRole === 'hr' ? allEmployees : teamMembers)?.find(m => m.id === option.value);
                                if (member) {
                                    const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
                                    const employeeId = member.employeeId?.toLowerCase() || '';
                                    return fullName.includes(searchText) || employeeId.includes(searchText);
                                }
                                return false;
                            }}
                        >
                            {(userRole === 'admin' || userRole === 'hr' ? allEmployees : teamMembers)?.map((member) => (
                                <Option key={member.id} value={member.id}>
                                    {member.firstName} {member.lastName} ({member.employeeId})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}

                <Form.Item
                    label="Request Type"
                    name="requestType"
                    rules={[{ required: true, message: 'Please select request type!' }]}
                >
                    <Radio.Group onChange={handleLoanTypeChange} value={loanType}>
                        {/* Loan option commented out — Advance Payment only */}
                        {/* <Radio value="loan">Loan</Radio> */}
                        <Radio value="advance">Advance Payment</Radio>
                    </Radio.Group>
                </Form.Item>

                {/* {showContractWarning() && (
                    <Alert
                        message="Contract Details Required"
                        description={
                            <div>
                                <p style={{ margin: '0 0 8px 0' }}>
                                    Contract details are not found for the selected employee, contact to admin.
                                    Please select a different employee or contact admin to add contract details.
                                </p>
                                <Button 
                                    size="small" 
                                    type="link" 
                                    style={{ padding: 0, height: 'auto' }}
                                    onClick={() => {
                                        setSelectedEmployee(null);
                                        form.setFieldsValue({
                                            employeeId: undefined,
                                            requestedAmount: undefined,
                                            returningMonths: undefined
                                        });
                                    }}
                                >
                                    Clear employee selection
                                </Button>
                            </div>
                        }
                        type="warning"
                        icon={<ExclamationCircleOutlined />}
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )} */}

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            label="Advance Amount (£)"
                            name="requestedAmount"
                            help={loanType === 'advance' && getEmployeeMonthlySalary(selectedEmployee || userId) ? 
                                `Maximum allowed: £${getEmployeeMonthlySalary(selectedEmployee || userId).toLocaleString()}` : 
                                null
                            }
                            rules={[
                                { required: true, message: 'Please enter the advance amount!' },
                                { type: 'number', min: 1, message: 'Amount must be greater than 0!' },
                                {
                                    validator: (_, value) => {
                                        if (loanType === 'advance' && value) {
                                            const maxAmount = getEmployeeMonthlySalary(selectedEmployee || userId);
                                            if (maxAmount && value > maxAmount) {
                                                return Promise.reject(new Error(`Amount cannot exceed monthly salary of £${maxAmount.toLocaleString()}`));
                                            }
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <InputNumber
                                placeholder="Enter amount (max: monthly salary)"
                                prefix={<PoundOutlined />}
                                style={{ width: '100%' }}
                                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                min={1}
                                max={loanType === 'advance' ? getEmployeeMonthlySalary(selectedEmployee || userId) : undefined}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label="Returning Months"
                            name="returningMonths"
                            rules={[
                                { required: true, message: 'Please enter the number of months to return the advance!' },
                                { type: 'number', min: 1, message: 'Months must be at least 1!' },
                            ]}
                        >
                            <InputNumber
                                placeholder="Fixed at 1 month for advance payment"
                                style={{ width: '100%' }}
                                min={1}
                                max={120}
                                disabled={loanType === 'advance'}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label="Purpose Category"
                            name="purposeCategory"
                            rules={loanType === 'advance' ? [] : [{ required: true, message: 'Please select purpose category!' }]}
                        >
                            <Select placeholder="Select purpose category">
                                <Option value="home">Home Purchase/Renovation</Option>
                                <Option value="education">Education</Option>
                                <Option value="medical">Medical Emergency</Option>
                                <Option value="vehicle">Vehicle Purchase</Option>
                                <Option value="personal">Personal</Option>
                                <Option value="other">Other</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    label="Detailed Purpose"
                    name="purpose"
                    rules={loanType === 'advance' ? [] : [
                        { required: true, message: 'Please enter the purpose of the advance!' },
                        { min: 10, message: 'Purpose must be at least 10 characters long!' },
                    ]}
                >
                    <TextArea
                        placeholder="Optional: Provide detailed description of the advance payment purpose..."
                        rows={4}
                        prefix={<FileTextOutlined />}
                        showCount
                        maxLength={500}
                    />
                </Form.Item>

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        style={{ width: '100%' }}
                        size="large"
                        disabled={showContractWarning()}
                    >
                        Submit Advance Payment Request
                    </Button>
                </Form.Item>
            </Form>

            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f6f8fa', borderRadius: '6px' }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#24292f' }}>Important Information:</h4>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#656d76' }}>
                    <li>Your advance payment request will be reviewed by your reporting manager</li>
                    <li>You will receive email notifications about the status</li>
                    <li>Only one pending request is allowed at a time</li>
                    <li>Advance payment requests have simplified requirements and faster processing</li>
                    {canApplyForOthers && (
                        <li>You can apply on behalf of your team members</li>
                    )}
                </ul>
            </div>
        </Card>
    );
};

export default ApplyLoan;
